import React, {
  useRef, useEffect, useMemo, forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import cns from 'classnames';

import DeleteIcon from '../Icons/DeleteIcon';
import Spinner from '../Spinner';
import DontShowAgain from '../DontShowAgain/DontShowAgain';
import { usePortalElement, useChangeModalPosition, useBodyModalOpenClass } from './hooks';
import Body from './Body';
import Header from './Header';
import Footer from './Footer';
import useModalContext from '../ModalContext/useModalContext';
import { sameType } from '../../utils/react';
import { NOOP } from '../../utils/const';
import useDontShowAgainMessageRead from '../DontShowAgain/useDontShowAgainMessageRead';

const ModalCloseButton = ({ onClose }) => (
  <div
    className="modal__close"
    onClick={onClose}
    role="presentation"
  >
    <DeleteIcon />
  </div>
);

ModalCloseButton.propTypes = {
  /**
   * Callback di chiusura della modale
   */
  onClose: PropTypes.func,
};

ModalCloseButton.defaultProps = {
  onClose: null,
};

const ModalContent = forwardRef((props, ref) => {
  const {
    children,
    className,
    x, y,
    style,
    ...others
  } = props;
  return (
    <div
      className={className}
      style={{
        marginLeft: x,
        marginTop: y,
        ...style,
      }}
      ref={ref}
      tabIndex="-1"
      {...others} // eslint-disable-line react/jsx-props-no-spreading
    >
      {children}
    </div>
  );
});

ModalContent.propTypes = {
  /** Contenuto da renderizzare all'interno del componente */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string.isRequired,
  /** Indica la posizione del contenuto */
  x: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  y: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  /** stile personalizzabile da aggiungere al componente */
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

ModalContent.defaultProps = {
  style: {},
};

/**
Presenta una finestra modale con Body, Header e Footer.
Crea un elemento a livello body su cui renderizzare la modale.
 */
const Modal = (props) => {
  const {
    children,
    className,
    contentClassName,
    doNotFocus,
    initialPos,
    loading,
    onClose,
    show,
    style,
    dontShowAgainId,
    ...others
  } = props;

  const contentRef = useRef();
  const portalElementRef = usePortalElement(show);
  const {
    x, y, overflow, maxHeight, dragging, onMouseDown,
  } = useChangeModalPosition(initialPos, contentRef, portalElementRef);
  useBodyModalOpenClass(show);

  useModalContext(show);

  const messageRead = useDontShowAgainMessageRead(dontShowAgainId, show);

  useEffect(() => {
    if (!doNotFocus && contentRef.current) contentRef.current.focus();
  }, [doNotFocus]);

  const modalCloseButtonElement = useMemo(() => <ModalCloseButton onClose={onClose} />, [onClose]);

  const childrenWithProps = useMemo(() => {
    const newChildren = [];
    React.Children.forEach(
      children,
      (child) => {
        if (sameType(child, Body)) {
          newChildren.push(React.cloneElement(child, {
            overflow,
            maxHeight,
            key: newChildren.length + 1,
          }));
          if (dontShowAgainId) {
            newChildren.push(<DontShowAgain className="modal-dontshowagain" id={dontShowAgainId} />);
          }
        } else if (sameType(child, Header)) {
          newChildren.push(React.cloneElement(child, {
            dragging,
            onMouseDown,
            key: newChildren.length + 1,
          }));
        } else {
          newChildren.push(child);
        }
      },
    );
    return newChildren;
  }, [children, overflow, maxHeight, dontShowAgainId, dragging, onMouseDown]);

  if (!show) return null;
  if (messageRead) {
    onClose();
    return null;
  }

  const classes = cns('modal', className);
  const contentClasses = cns('modal--content', contentClassName);

  return createPortal(
    (
      <div className={classes}>
        <div className="modal--overlay" />
        <div className="modal--dialog">
          <ModalContent
            className={contentClasses}
            x={x}
            y={y}
            style={style}
            ref={contentRef}
            {...others} // eslint-disable-line react/jsx-props-no-spreading
          >
            {modalCloseButtonElement}
            {childrenWithProps}
            {loading && <div className="modal__loader"><Spinner large /></div>}
          </ModalContent>
        </div>
      </div>
    ),
    portalElementRef.current,
  );
};

Modal.propTypes = {
  /** Contenuto da renderizzare all'interno del componente */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Class css dal aggiungere a modal--content */
  contentClassName: PropTypes.string,
  /** Indica se aprire la modale non in focus */
  doNotFocus: PropTypes.bool,
  /** Indica la posizione iniziale di apertura della modale */
  initialPos: PropTypes.shape({
    x: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    y: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
  /** Indica se mostrare un'animazione di caricamento */
  loading: PropTypes.bool,
  /**
   * Callback di chiusura della modale:
   * ```
   *  onClose(event)
   * ```
   * dove:
   * * *event*: evento scatenante. Potrebbe essere undefined se chiusa per la gestione dontShowAgain.
   */
  onClose: PropTypes.func,
  /** Indica se mostrare o meno la modale */
  show: PropTypes.bool,
  /** stile personalizzabile da aggiungere al componente */
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
Identificativo del componente per la gestione 'Non visualizzare più'. Se impostato abilita la gestione.
Lo stato è salvato nella sessione locale del browser quindi è necessario un identificativo per gestire il salvataggio.

Tipicamente è composto dall'id dell'utente e da un identificativo del messaggio.
*/
  dontShowAgainId: PropTypes.string,
};

Modal.defaultProps = {
  children: null,
  className: '',
  contentClassName: '',
  doNotFocus: false,
  initialPos: {
    x: 'auto',
    y: (window.innerHeight * 0.05),
  },
  loading: false,
  onClose: NOOP,
  show: false,
  style: {},
  dontShowAgainId: undefined,
};

Modal.Body = Body;
Modal.Header = Header;
Modal.Footer = Footer;
Modal.clearMessageStatus = DontShowAgain.clearMessageStatus;

export default Modal;
